/**
 * Per-job guest memory telemetry in the management UI (plan:
 * internal/compiler/plans/2026-10-14-job-memory-telemetry.md).
 *
 * The runner's guest init samples the build every 60 s (guest used memory,
 * the build tree's summed RSS, the largest process) and records phase
 * boundaries (compiler stages, cargo bands, link). The API stores the
 * summary in `jobs.mem_*` columns (admin list + detail) and the timeseries
 * as JSON in `mem_telemetry` (detail only). This file is the PURE logic —
 * parsing, formatting, the red threshold and the chart geometry — so it is
 * unit-tested with node:test (memTelemetry.test.ts); components/MemoryPanel
 * only turns the model into SVG.
 */

/** Peak above this share of the guest's memory is shown red (the user's
 *  number: an OOM is imminent well before 100% because MemAvailable counts
 *  reclaimable cache). */
export const RED_PCT = 85;
/** Amber from here. */
export const WARN_PCT = 70;

export interface MemSample {
  t: number;
  u: number;
  r: number;
  c?: number;
  pn?: string;
  pr?: number;
  pc?: string;
  ph?: string;
  f?: 1;
}

export interface MemPhase {
  n: string;
  s: number;
  e?: number;
  pk: number;
  pt: number;
  pn?: string;
  pr?: number;
  pc?: string;
  open?: true;
}

/** Mirrors internal/api shared/protocol.ts MemTelemetry. */
export interface MemTelemetry {
  v: 1;
  guest_mem_mb: number;
  peak_rss_mb: number;
  peak_tree_rss_mb: number;
  peak_cgroup_mb?: number;
  peak_process?: { name: string; rss_mb: number; crate?: string };
  complete: boolean;
  interval_s: number;
  last_t: number;
  samples: MemSample[];
  phases: MemPhase[];
  host_peak_mb?: number;
  /** Mirrors internal/api shared/protocol.ts ColdUnits: cargo units this build
   *  compiled instead of reusing from the warm-target snapshot. */
  cold_units?: {
    v: 1;
    fresh?: number;
    compiled: number;
    cold: string[];
    truncated?: true;
    dirty?: Array<{ unit: string; reason: string }>;
    source: "marker" | "compiling-lines";
  };
  /** Mirrors internal/api shared/protocol.ts WarmSeed: where the job's cargo
   *  target dir came from. Absent on a runner that predates it. */
  warm_seed?: {
    key: string | null;
    seeded: boolean;
    snapshot_created_at?: string;
    why?: string;
  };
}

/** The summary columns of an admin jobs-list row (internal/api migration 0017). */
export interface MemSummaryFields {
  mem_peak_mb?: number | null;
  mem_peak_process?: string | null;
  mem_peak_process_mb?: number | null;
  mem_guest_mb?: number | null;
  mem_complete?: number | null;
}

export type MemLevel = "ok" | "warn" | "red";

export interface MemSummary {
  peakMb: number;
  /** "rustc (pure_runtime)" */
  process: string | null;
  processMb: number | null;
  guestMb: number | null;
  /** peak as % of the guest's memory (null when the guest size is unknown) */
  pct: number | null;
  level: MemLevel;
  /** false = the guest ended without a final report */
  complete: boolean;
}

/** null = no memory data (runner predates the sampler / job never ran). */
export function memSummary(row: MemSummaryFields): MemSummary | null {
  const peak = row.mem_peak_mb;
  if (peak == null || !Number.isFinite(peak)) return null;
  const guest =
    row.mem_guest_mb && row.mem_guest_mb > 0 ? row.mem_guest_mb : null;
  const pct = guest ? (peak / guest) * 100 : null;
  return {
    peakMb: peak,
    process: row.mem_peak_process ?? null,
    processMb: row.mem_peak_process_mb ?? null,
    guestMb: guest,
    pct,
    level: levelOf(pct),
    complete: row.mem_complete === 1,
  };
}

export function levelOf(pct: number | null): MemLevel {
  if (pct == null) return "ok";
  if (pct > RED_PCT) return "red";
  if (pct >= WARN_PCT) return "warn";
  return "ok";
}

/** "812 MB", "1.4 GB", "17.1 GB" (binary units, shown MB/GB like the rest of the UI). */
export function fmtMem(mb: number | null | undefined): string {
  if (mb == null || !Number.isFinite(mb)) return "—";
  if (mb < 1024) return `${Math.round(mb)} MB`;
  const gb = mb / 1024;
  return `${gb < 10 ? gb.toFixed(2) : gb.toFixed(1)} GB`;
}

export function fmtPct(p: number | null): string {
  return p == null ? "" : `${p < 10 ? p.toFixed(1) : Math.round(p)}%`;
}

/** "3m 07s" / "41s" for a millisecond span. */
export function fmtSpanMs(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

/** Defensive parse of the detail row's `mem_telemetry` JSON string. */
export function parseMemTelemetry(
  raw: string | null | undefined,
): MemTelemetry | null {
  if (!raw) return null;
  let j: unknown;
  try {
    j = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!j || typeof j !== "object") return null;
  const o = j as Partial<MemTelemetry>;
  if (
    o.v !== 1 ||
    !Array.isArray(o.samples) ||
    typeof o.peak_rss_mb !== "number"
  )
    return null;
  return {
    ...(o as MemTelemetry),
    phases: Array.isArray(o.phases) ? o.phases : [],
    guest_mem_mb: typeof o.guest_mem_mb === "number" ? o.guest_mem_mb : 0,
  };
}

// ── phases ───────────────────────────────────────────────────────────────────

export type PhaseKind =
  | "transpile"
  | "cargo-deps"
  | "cargo-runtime"
  | "cargo-package"
  | "link"
  | "cargo"
  | "other";

/** Which band colour/legend a phase name gets. Names come from the compiler
 *  markers ("Stage 4: emit (codegen)", "cargo build") and the guest's cargo
 *  bands ("cargo: pure_runtime", "cargo: deps", "cargo: package crate", "link"). */
export function phaseKind(name: string): PhaseKind {
  if (name === "link") return "link";
  if (name === "cargo: pure_runtime") return "cargo-runtime";
  if (name === "cargo: package crate") return "cargo-package";
  if (name === "cargo: deps") return "cargo-deps";
  if (name === "cargo build") return "cargo";
  if (/^Stage \d/.test(name) || name === "startup") return "transpile";
  return "other";
}

/** Tailwind classes (literal strings so Tailwind keeps them) per kind. */
export const PHASE_FILL: Record<PhaseKind, string> = {
  transpile: "fill-blue-500/15",
  "cargo-deps": "fill-amber-500/20",
  "cargo-runtime": "fill-violet-500/20",
  "cargo-package": "fill-emerald-500/20",
  link: "fill-rose-500/20",
  cargo: "fill-amber-500/10",
  other: "fill-slate-500/10",
};

export const PHASE_SWATCH: Record<PhaseKind, string> = {
  transpile: "bg-blue-500/30",
  "cargo-deps": "bg-amber-500/40",
  "cargo-runtime": "bg-violet-500/40",
  "cargo-package": "bg-emerald-500/40",
  link: "bg-rose-500/40",
  cargo: "bg-amber-500/20",
  other: "bg-slate-500/25",
};

export const PHASE_LEGEND: Array<{ kind: PhaseKind; label: string }> = [
  { kind: "transpile", label: "transpile" },
  { kind: "cargo-runtime", label: "cargo: pure_runtime" },
  { kind: "cargo-deps", label: "cargo: deps" },
  { kind: "cargo-package", label: "cargo: package crate" },
  { kind: "link", label: "link" },
];

export interface PhaseRow {
  name: string;
  kind: PhaseKind;
  startMs: number;
  endMs: number;
  durationMs: number;
  /** still open when the data ends (guest died mid-phase) */
  open: boolean;
  peakMb: number;
  peakTreeMb: number;
  process: string | null;
  processMb: number | null;
  /** peak as % of the guest memory */
  pct: number | null;
  level: MemLevel;
}

/** Phases as table rows, in time order, with their own peak. */
export function phaseRows(t: MemTelemetry): PhaseRow[] {
  const endOfData = Math.max(
    t.last_t * 1000,
    ...t.samples.map((s) => s.t * 1000),
  );
  return [...t.phases]
    .sort((a, b) => a.s - b.s)
    .map((p) => {
      const end = p.e ?? Math.max(endOfData, p.s);
      const pct = t.guest_mem_mb > 0 ? (p.pk / t.guest_mem_mb) * 100 : null;
      return {
        name: p.n,
        kind: phaseKind(p.n),
        startMs: p.s,
        endMs: end,
        durationMs: Math.max(0, end - p.s),
        open: p.open === true || p.e === undefined,
        peakMb: p.pk,
        peakTreeMb: p.pt,
        process: p.pn ? (p.pc ? `${p.pn} (${p.pc})` : p.pn) : null,
        processMb: p.pr ?? null,
        pct,
        level: levelOf(pct),
      };
    });
}

// ── chart geometry ───────────────────────────────────────────────────────────

export interface ChartBand {
  x: number;
  w: number;
  kind: PhaseKind;
  name: string;
  /** tooltip text */
  title: string;
  /** label fits inside the band */
  showLabel: boolean;
  open: boolean;
}

export interface ChartTick {
  pos: number;
  label: string;
}

export interface ChartModel {
  width: number;
  height: number;
  /** plot rectangle */
  plot: { x: number; y: number; w: number; h: number };
  /** polyline paths ("" when there is nothing to draw) */
  usedPath: string;
  treePath: string;
  /** sample dots (x,y) of the used line, so 1-2 samples still show */
  dots: Array<{ x: number; y: number; title: string }>;
  bands: ChartBand[];
  /** guest MemTotal limit line y (null when unknown) */
  limitY: number | null;
  /** the 85% line y */
  redY: number | null;
  /** peak marker */
  peak: {
    x: number;
    y: number;
    label: string;
    level: MemLevel;
    betweenSamples: boolean;
  };
  yTicks: ChartTick[];
  xTicks: ChartTick[];
  /** true when the data ends without a final report */
  incomplete: boolean;
  /** x of the end of the data (where an incomplete series stops) */
  endX: number;
  yMaxMb: number;
  xMaxS: number;
}

export interface ChartOpts {
  width: number;
  height: number;
}

const PAD = { l: 52, r: 14, t: 14, b: 24 };

function niceStepMb(maxMb: number, target = 4): number {
  const raw = maxMb / target;
  const steps = [
    128, 256, 512, 1024, 2048, 4096, 5120, 8192, 10240, 16384, 20480, 32768,
    65536,
  ];
  for (const s of steps) if (raw <= s) return s;
  return 65536;
}

function niceStepS(maxS: number, target = 5): number {
  const raw = maxS / target;
  const steps = [60, 120, 300, 600, 900, 1800, 3600, 7200, 14400];
  for (const s of steps) if (raw <= s) return s;
  return 14400;
}

function r1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** The chart for one job. Works with a single sample (x domain is at least
 *  one sampling interval) and with a dead guest (`incomplete`). */
export function buildChartModel(t: MemTelemetry, opts: ChartOpts): ChartModel {
  const width = Math.max(240, Math.round(opts.width));
  const height = Math.max(140, Math.round(opts.height));
  const plot = {
    x: PAD.l,
    y: PAD.t,
    w: width - PAD.l - PAD.r,
    h: height - PAD.t - PAD.b,
  };

  const lastSampleT = t.samples.reduce((m, s) => Math.max(m, s.t), 0);
  const phaseEndS = t.phases.reduce(
    (m, p) => Math.max(m, (p.e ?? p.s) / 1000),
    0,
  );
  const dataEndS = Math.max(lastSampleT, t.last_t, phaseEndS);
  const interval = t.interval_s > 0 ? t.interval_s : 60;
  const xMaxS = Math.max(dataEndS, interval);

  const maxSampleU = t.samples.reduce((m, s) => Math.max(m, s.u, s.r), 0);
  const topMb = Math.max(t.guest_mem_mb, t.peak_rss_mb, maxSampleU, 256);
  const step = niceStepMb(topMb);
  const yMaxMb = Math.max(step, Math.ceil((topMb * 1.04) / step) * step);

  const X = (s: number) => r1(plot.x + (s / xMaxS) * plot.w);
  const Y = (mb: number) => r1(plot.y + plot.h - (mb / yMaxMb) * plot.h);

  const pts = [...t.samples].sort((a, b) => a.t - b.t);
  const line = (sel: (s: MemSample) => number) =>
    pts.length === 0
      ? ""
      : pts
          .map((s, i) => `${i === 0 ? "M" : "L"}${X(s.t)} ${Y(sel(s))}`)
          .join(" ");

  const bands: ChartBand[] = phaseRows(t).map((p) => {
    const x0 = X(p.startMs / 1000);
    const x1 = X(p.endMs / 1000);
    const w = Math.max(1, r1(x1 - x0));
    const proc = p.process
      ? `; largest ${p.process} ${fmtMem(p.processMb)}`
      : "";
    return {
      x: x0,
      w,
      kind: p.kind,
      name: p.name,
      title: `${p.name}: ${fmtSpanMs(p.durationMs)}${p.open ? " (guest ended in this phase)" : ""}, peak ${fmtMem(p.peakMb)}${proc}`,
      showLabel: w >= 54,
      open: p.open && !t.complete,
    };
  });

  // Peak marker: at the sample that holds the max used value; if the guest's
  // own (2 s) peak is higher than every stored sample, the spike fell between
  // samples — mark it at the end of the data and say so.
  let top: MemSample | undefined;
  for (const s of pts) if (!top || s.u > top.u) top = s;
  const between = !top || t.peak_rss_mb > top.u;
  const peakX = between ? X(dataEndS) : X(top!.t);
  const pct =
    t.guest_mem_mb > 0 ? (t.peak_rss_mb / t.guest_mem_mb) * 100 : null;
  const level = levelOf(pct);
  const procLabel = t.peak_process
    ? ` · ${t.peak_process.name}${t.peak_process.crate ? ` (${t.peak_process.crate})` : ""} ${fmtMem(t.peak_process.rss_mb)}`
    : "";

  const yTicks: ChartTick[] = [];
  for (let v = 0; v <= yMaxMb + 1; v += step)
    yTicks.push({ pos: Y(v), label: v === 0 ? "0" : fmtMem(v) });

  const xStep = niceStepS(xMaxS);
  const xTicks: ChartTick[] = [];
  for (let s = 0; s <= xMaxS + 1; s += xStep)
    xTicks.push({ pos: X(s), label: s === 0 ? "0" : fmtSpanMs(s * 1000) });

  return {
    width,
    height,
    plot,
    usedPath: line((s) => s.u),
    treePath: line((s) => s.r),
    dots: pts.map((s) => ({
      x: X(s.t),
      y: Y(s.u),
      title: `${fmtSpanMs(s.t * 1000)}: ${fmtMem(s.u)} used, build tree ${fmtMem(s.r)}${s.pn ? `, largest ${s.pn}${s.pc ? ` (${s.pc})` : ""} ${fmtMem(s.pr)}` : ""}${s.ph ? ` [${s.ph}]` : ""}`,
    })),
    bands,
    limitY: t.guest_mem_mb > 0 ? Y(t.guest_mem_mb) : null,
    redY: t.guest_mem_mb > 0 ? Y((t.guest_mem_mb * RED_PCT) / 100) : null,
    peak: {
      x: peakX,
      y: Y(t.peak_rss_mb),
      label: `peak ${fmtMem(t.peak_rss_mb)}${pct != null ? ` (${fmtPct(pct)})` : ""}${procLabel}`,
      level,
      betweenSamples: between,
    },
    yTicks,
    xTicks,
    incomplete: !t.complete,
    endX: X(dataEndS),
    yMaxMb,
    xMaxS,
  };
}

/** Why there is no memory data, for the job/registry rows. */
export function noDataText(status: string): string {
  return status === "queued" || status === "running"
    ? "memory data appears when the build ends"
    : "no memory data (runner predates the sampler)";
}
